import React, { useRef, useState } from 'react';
import { EditorState, Modifier, RichUtils } from 'draft-js';
import { useOutsideClick } from "ubs-framework/v2/hooks";

/*** Font family + font size pickers for the Draft editors */

const FAMILY_LIST = [
    { label: 'Mulish', value: '' },
    { label: 'Sans Serif', value: 'sans-serif' },
    { label: 'Andale Mono', value: 'Andale Mono' },
    { label: 'Arial', value: 'Arial' },
    { label: 'Arial Black', value: 'Arial Black' },
    { label: 'Book Antiqua', value: 'Book Antiqua' },
    { label: 'Calibri', value: 'Calibri' },
    { label: 'Comic Sans MS', value: 'Comic Sans MS' },
    { label: 'Courier New', value: 'Courier New' },
    { label: 'Georgia', value: 'Georgia' },
    { label: 'Helvetica', value: 'Helvetica' },
    { label: 'Impact', value: 'Impact' },
    { label: 'Tahoma', value: 'Tahoma' },
    { label: 'Times New Roman', value: 'Times New Roman' },
    { label: 'Trebuchet MS', value: 'Trebuchet MS' },
    { label: 'Verdana', value: 'Verdana' },
    { label: 'Wingdings', value: 'Wingdings' },
];

const SIZE_LIST = [8, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36].map(px => px === 16
    ? { label: `${px}px`, value: '' }
    : { label: `${px}px`, value: `${px}`, css: `${px}px` });

const firstFamily = (value) => (value || '').split(',')[0]
    .replace(/&quot;|&#0?39;|&apos;|["']/gi, '').trim().toLowerCase();

const buildOptions = (list, prefix, cssProp) => list.map(option => ({
    ...option,
    style: option.value ? `${prefix}-${option.value}` : '',
    css: option.value ? { [cssProp]: option.css || option.value } : null,
}));

const FONT_FAMILIES = buildOptions(FAMILY_LIST, 'fontfamily', 'fontFamily');
const FONT_SIZES = buildOptions(SIZE_LIST, 'fontsize', 'fontSize');

export const FONT_STYLE_MAP = {};
const FAMILY_BY_FIRST_NAME = {};
const SIZE_BY_CSS = {};

const indexByStyle = (options) => {
    const byStyle = {};
    options.forEach(option => {
        if (!option.style) return;
        FONT_STYLE_MAP[option.style] = option.css;
        byStyle[option.style] = option;
    });
    return byStyle;
};

const FAMILY_BY_STYLE = indexByStyle(FONT_FAMILIES);
const SIZE_BY_STYLE = indexByStyle(FONT_SIZES);

FONT_FAMILIES.forEach(option => option.style && (FAMILY_BY_FIRST_NAME[firstFamily(option.value)] = option));
FONT_SIZES.forEach(option => option.style && (SIZE_BY_CSS[option.css.fontSize] = option));

const defaultOption = (options) => options.find(option => !option.style);

const DEFAULT_FAMILY = defaultOption(FONT_FAMILIES);
const DEFAULT_SIZE = defaultOption(FONT_SIZES);

const activeStyleKey = (byStyle, inlineStyle) => inlineStyle?.find(style => byStyle[style]) || '';

const applyStyle = (editorState, byStyle, styleKey) => {
    const selection = editorState.getSelection();
    let newState = editorState;

    if (selection.isCollapsed()) {
        const active = activeStyleKey(byStyle, editorState.getCurrentInlineStyle());
        if (active) newState = RichUtils.toggleInlineStyle(editorState, active);
    } else {
        const content = Object.keys(byStyle)
            .reduce((_, key) => Modifier.removeInlineStyle(_, selection, key), editorState.getCurrentContent());
        newState = EditorState.forceSelection(EditorState.push(editorState, content, 'change-inline-style'), selection);
    }

    return styleKey ? RichUtils.toggleInlineStyle(newState, styleKey) : newState;
};

const FONT_FAMILY_DECLARATION = /font-family\s*:\s*((?:&[a-z0-9#]+;|[^;"'])+)/gi;
export const normalizeFontFamilyHTML = (html) => (html || '').replace(
    FONT_FAMILY_DECLARATION,
    (match, value) => {
        const option = FAMILY_BY_FIRST_NAME[firstFamily(value)];
        return option ? `font-family: ${option.value}` : match;
    }
);

export const fontStyleToHTML = (style) => {
    const css = FONT_STYLE_MAP[style];
    return css ? <span style={css} /> : undefined;
};

// draft-convert (edit): those spans -> font styles
export const fontStyleFromHTML = (nodeName, node, currentStyle) => {
    if (nodeName !== 'span') return currentStyle;

    const family = FAMILY_BY_FIRST_NAME[firstFamily(node?.style?.fontFamily)];
    const size = SIZE_BY_CSS[node?.style?.fontSize];

    let style = family ? currentStyle.add(family.style) : currentStyle;
    return size ? style.add(size.style) : style;
};

const StyleSelect = ({ options, byStyle, fallback, title, modifier, editorState, onChange, disabled = false }) => {
    const wrapperRef = useRef(null);
    const [open, setOpen] = useState(false);

    useOutsideClick(wrapperRef, () => setOpen(false), { enabled: open });

    const active = byStyle[activeStyleKey(byStyle, editorState?.getCurrentInlineStyle())] || fallback;
    const isDisabled = disabled || !editorState;

    const select = (option) => {
        setOpen(false);
        if (option.style === active.style) return;
        onChange(applyStyle(editorState, byStyle, option.style));
    };

    return (
        <div className={`u_font-select u_relative u_px-5 ${modifier} ${isDisabled ? 'u_disabled' : ''}`}
            ref={wrapperRef} onMouseDown={e => e.preventDefault()}>
            <div className="u_select__control u_flex u_align-center u_cp" title={title} onClick={() => setOpen(!open)}>
                <span className="u_select__single-value u_flex-1 u_pl-10 u_overflow-hidden">{active.label}</span>
                <span className="u_select__indicators">
                    <span className="u_select__dropdown-indicator" />
                </span>
            </div>
            {
                open &&
                <div className="u_select__menu u_absolute u_bg-white">
                    <ul className="u_select__menu-list u_overflow-y-auto">
                        {options.map(option => (
                            <li key={option.label}
                                className={`u_select__option u_cp u_pl-10 u_py-8 ${option.style === active.style ? 'u_select__option--is-selected' : ''}`}
                                onClick={() => select(option)}>{option.label}</li>
                        ))}
                    </ul>
                </div>
            }
        </div>
    );
};

/** Toolbar font family picker. */
export const FontFamilySelect = (props) => (
    <StyleSelect options={FONT_FAMILIES} byStyle={FAMILY_BY_STYLE} fallback={DEFAULT_FAMILY}
        title="Font style" modifier="" {...props} />
);

/** Toolbar font size picker*/
export const FontSizeSelect = (props) => (
    <StyleSelect options={FONT_SIZES} byStyle={SIZE_BY_STYLE} fallback={DEFAULT_SIZE}
        title="Font size" modifier="u_font-select--size" {...props} />
);
